import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { tariffApi } from '@/common/services/api'
import { getFirstDataFromObject } from '@/common/utils'
import { USD_CURRENCY } from '@/common/constants/currency'
import { UPDATE_SUBSCRIPTION_MODAL } from '@/common/constants/modals'
import { open } from '@/store/modalsSlice'

export const initTariffs = createAsyncThunk(
  'tariff/initTariffs',
  async (_, { rejectWithValue }) => {
    try {
      const [products, paymentMethods, payer] = await Promise.all([
        tariffApi.getTariffs(),
        tariffApi.getPaymentMethods(),
        tariffApi.getUserPayer(),
      ])

      return {
        products: products.products,
        paymentMethods: paymentMethods.payment_methods,
        payer: payer,
      }
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const initTariffsPublic = createAsyncThunk(
  'tariff/initTariffsPublic',
  async (_, { rejectWithValue }) => {
    try {
      const [products, payer] = await Promise.all([
        tariffApi.getTariffs(),
        tariffApi.getUserPublicPayer(),
      ])

      return {
        products: products.products,
        payer: payer,
      }
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const sendPromocode = createAsyncThunk(
  'tariff/sendPromocode',
  async (promocode, { rejectWithValue, dispatch }) => {
    try {
      const response = await tariffApi.sendPromocode(promocode)

      if (response.instant_apply) {
        dispatch(open(UPDATE_SUBSCRIPTION_MODAL))
      }

      return response
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const makePay = createAsyncThunk(
  'tariff/makePay',
  async (data, { rejectWithValue }) => {
    try {
      return await tariffApi.makePay({
        data,
      })
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

const initialState = {
  tariffsMap: null,
  promocodeDiscount: null,
  error: null,
  isLoadingTariffs: true,
  currentSelectedTariff: null,
  currentPayment: null,
  currentCurrency: null,
  defaultUserCurrency: null,
  country: null,
  availableCurrencies: [],
  promocode: null,
  presetPromocode: null,
  isLoadingPromocode: false,
  promocodeError: null,
  isPromocodeClear: false,
  availablePayments: {},
  isBlackFriday: true,
  planId: null,
}

const updateStateWithPayload = (state, payload) => {
  state.tariffsMap = payload.products

  if (!state.currentSelectedTariff) {
    state.currentSelectedTariff = getFirstDataFromObject(payload.products)
  }

  state.currentCurrency =
    payload.payer.preferences.preferred_currency ?? USD_CURRENCY
  state.defaultUserCurrency =
    payload.payer.preferences.preferred_currency ?? USD_CURRENCY
  state.availableCurrencies = payload.payer.preferences.currencies
  state.country = payload.payer.preferences.country?.toLowerCase() ?? 'us'

  const presetPromoCode = payload?.payer?.preset_promocode
  if (presetPromoCode && !state.isPromocodeClear) {
    state.presetPromocode = presetPromoCode
  }
}

export const tariffSlice = createSlice({
  name: 'tariff',
  initialState,
  reducers: {
    setCurrentTariff(state, { payload }) {
      state.currentSelectedTariff = payload
    },
    setCurrentPayment(state, { payload }) {
      state.currentPayment = payload
    },
    setCurrentCurrency(state, { payload }) {
      state.currentCurrency = payload
    },
    setCurrentPromocode(state, { payload }) {
      state.promocode = payload
    },
    setPromocodeError(state, { payload }) {
      state.promocodeError = payload
    },
    updateTariffsDiscount(state, { payload }) {
      state.tariffsMap = payload
    },
    setIsPromocodeClear(state, { payload }) {
      state.isPromocodeClear = payload
    },
    clearPromocode(state) {
      state.promocode = null
      state.promocodeDiscount = null
      state.presetPromocode = null
      state.isPromocodeClear = true
    },
  },
  extraReducers: {
    [initTariffs.pending]: (state) => {
      state.isLoadingTariffs = true
      state.error = null
    },
    [initTariffs.fulfilled]: (state, { payload }) => {
      try {
        updateStateWithPayload(state, payload)
        state.availablePayments = payload.paymentMethods
      } catch (e) {
        state.error = e
        throw e
      } finally {
        state.isLoadingTariffs = false
      }
    },
    [initTariffsPublic.fulfilled]: (state, { payload }) => {
      try {
        updateStateWithPayload(state, payload)
      } catch (e) {
        state.error = e
        throw e
      } finally {
        state.isLoadingTariffs = false
      }
    },
    [initTariffs.rejected]: (state, { payload }) => {
      state.isLoadingTariffs = false
      state.tariffsMap = null
      state.error = payload
      state.country = 'us'
    },
    [initTariffsPublic.rejected]: (state, { payload }) => {
      state.isLoadingTariffs = false
      state.tariffsMap = null
      state.error = payload
      state.country = 'us'
    },
    [sendPromocode.pending]: (state) => {
      state.isLoadingPromocode = true
    },
    [sendPromocode.fulfilled]: (state, { payload }) => {
      state.isLoadingPromocode = false
      state.promocode = payload
      state.promocodeDiscount = payload
    },
    [sendPromocode.rejected]: (state, { payload }) => {
      state.isLoadingPromocode = false
      state.promocodeError = payload?.data?.error
    },
  },
})

export const tariffActions = tariffSlice.actions

export default tariffSlice.reducer
